import React, { useEffect, useState } from "react";
import { Stack, Text, TextInput, Title } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";

const FIELDS = [
  {
    key: "imapPassword",
    label: "IMAP password",
    description: "Mailbox used to read insurer emails. The username stays in the server config.",
  },
  {
    key: "openrouterApiKey",
    label: "OpenRouter API key",
    description: "Used by AI email and document parsing.",
  },
  {
    key: "baiduApiKey",
    label: "Baidu OCR API key",
  },
  {
    key: "baiduSecretKey",
    label: "Baidu OCR secret key",
  },
  {
    key: "cmApiKey",
    label: "Case Management API key",
  },
  {
    key: "cmUserPassword",
    label: "Case Management user password",
  },
  {
    key: "fwdNotificationApiKey",
    label: "FWD notification API key",
  },
];

const emptyValues = () =>
  FIELDS.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});

const IntegrationKeySetting = () => {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const tabNav = useTabNavigate();
  const [values, setValues] = useState(emptyValues);
  const [configured, setConfigured] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applySettings = (data) => {
    if (!data) return;
    const next = {};
    FIELDS.forEach((field) => {
      next[field.key] = data[`${field.key}Configured`] === true;
    });
    setConfigured(next);
  };

  useEffect(() => {
    let active = true;
    const fetchSetting = async () => {
      try {
        setLoading(true);
        const res = await api.integrationSettings.get();
        if (active && res?.success) applySettings(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSetting();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {};
      FIELDS.forEach((field) => {
        const value = String(values[field.key] || "").trim();
        if (value) payload[field.key] = value;
      });
      const res = await api.integrationSettings.save(payload);
      if (res?.success) {
        setValues(emptyValues());
        applySettings(res.data);
        showNotification({
          title: t("Saved"),
          message: t("Integration keys saved."),
          color: "green",
          icon: <IconCircleCheck />,
        });
        return;
      }
      showNotification({
        title: t("Error"),
        message:
          res?.error?.msg || res?.message || t("Could not save integration keys."),
        color: "red",
        icon: <IconExclamationMark />,
      });
    } catch (error) {
      showNotification({
        title: t("Error"),
        message: error.message,
        color: "red",
        icon: <IconExclamationMark />,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("Integration Keys")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled: loading,
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />
      {!loading && (
        <Stack spacing="md">
          <FormSectionCard>
            <Title order={5}>{t("Stored keys")}</Title>
            <Text size="xs" color="dimmed" mb="md">
              {t(
                "A saved key is stored encrypted and used instead of the server config file. Leave a box empty to keep the current value. If nothing has been saved, the config file is still used."
              )}
            </Text>
            {FIELDS.map((field) => (
              <TextInput
                key={field.key}
                label={t(field.label)}
                description={
                  field.description
                    ? t(field.description)
                    : configured[field.key]
                      ? t("A key is already stored. Leave blank to keep it.")
                      : t("Not stored yet. The server config file is used.")
                }
                type="password"
                autoComplete="new-password"
                mb="md"
                value={values[field.key]}
                placeholder={
                  configured[field.key]
                    ? t("Leave blank to keep the current key")
                    : ""
                }
                onChange={(event) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.key]: event.currentTarget.value,
                  }))
                }
              />
            ))}
          </FormSectionCard>
        </Stack>
      )}
    </Page>
  );
};

export default IntegrationKeySetting;
